"use client";

import { useState } from "react";
import { AttendanceStatus } from "@prisma/client";
import { updateAttendeeStatus } from "~/components/rsvp/actions/change-attendee-status";
import {
    DefaultUserValues,
    RSVPButtonControlData,
} from "~/components/rsvp/actions/rsvp-steps-and-data";
import RSVPModalStepControl from "~/components/rsvp/RSVPModalStepControl";
import { ProfileEditRequiredData } from "~/components/rsvp/validators";
import { AddToCalendarButtons } from "~/components/themed/AddToCalendar";
import { toastHandler } from "~/components/ToastHandler";
import { Button } from "~/components/v4/themed/Button";
import { useEventContextV2 } from "~/app/providers";
import { RSVPWrapperCustomization } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";

interface RSVPButtonControlProps {
    eventSlug: string;
    rsvpControlData: RSVPButtonControlData;
    missingUserData: ProfileEditRequiredData | undefined;
    defaultUserValues: DefaultUserValues;
    refetchAction?: () => Promise<void>;
    hasPaidForTicket: boolean;
    customization?: RSVPWrapperCustomization;
}

export const RSVPButtonControl = ({
    eventSlug,
    rsvpControlData,
    missingUserData,
    defaultUserValues,
    refetchAction,
    hasPaidForTicket,
    customization,
}: RSVPButtonControlProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const event = useEventContextV2();

    const {
        steps,
        formInputSteps,
        questionnaires,
        communityTerms,
        membershipList,
        eventInfo,
    } = rsvpControlData;

    const handleStatusChange = async (status: AttendanceStatus) => {
        setIsLoading(true);
        await toastHandler({
            loadingMessage: "Updating status",
            successMessage: "Updated!",
            operation: async () => {
                await updateAttendeeStatus(event.id, status);
            },
            onSettled: async () => {
                setIsLoading(false);
                await refetchAction?.();
            },
        });
    };

    if (steps.includes("HOSTING")) {
        return (
            <div
                className={cn(
                    "flex w-full flex-col gap-2 rounded-[10px] bg-v3-N-500 p-4",
                    customization?.mainCardClassName,
                )}
            >
                <h6 className={cn("text-xl", customization?.labelClassName)}>
                    You're hosting this event!
                </h6>
                <p
                    className={cn(
                        "text-v3-N-300",
                        customization?.descriptionClassName,
                    )}
                >
                    Make sure to promote the event to your community!
                </p>
                <div>
                    <AddToCalendarButtons
                        event={eventInfo.event}
                        customization={customization?.buttons}
                    />
                </div>
            </div>
        );
    }

    if (steps.includes("MEMBERSHIP_REQUIRED")) {
        return (
            <div
                className={cn(
                    "flex w-full flex-col gap-2 rounded-[10px] bg-v3-N-500 p-4",
                    customization?.mainCardClassName,
                )}
            >
                <h6 className={cn(customization?.labelClassName)}>
                    Membership required
                </h6>
                <p
                    className={cn(
                        "text-v3-N-300",
                        customization?.descriptionClassName,
                    )}
                >
                    You need to be a member of this community to join this
                    Event.
                </p>
            </div>
        );
    }

    if (steps.includes("TOKEN_REQUIRED")) {
        return (
            <div
                className={cn(
                    "flex w-full flex-col gap-2 rounded-[10px] bg-v3-N-500 p-4",
                    customization?.mainCardClassName,
                )}
            >
                <h6 className={cn(customization?.labelClassName)}>
                    Token required
                </h6>
                <p
                    className={cn(
                        "text-v3-N-300",
                        customization?.descriptionClassName,
                    )}
                >
                    You need to be a holder of a token to join this Event.
                </p>
            </div>
        );
    }

    if (steps.includes("SELECT_TICKET") && hasPaidForTicket) {
        return (
            <div
                className={cn(
                    "flex w-full flex-col gap-2 rounded-[10px] bg-v3-N-500 p-4",
                    customization?.mainCardClassName,
                )}
            >
                <h6 className={cn(customization?.labelClassName)}>
                    Join the event
                </h6>
                <p
                    className={cn(
                        "text-v3-N-300",
                        customization?.descriptionClassName,
                    )}
                >
                    You purchased a ticket to this event.
                </p>
                <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => handleStatusChange("ATTENDING")}
                >
                    Join the event
                </Button>
            </div>
        );
    }

    return (
        <RSVPModalStepControl
            eventSlug={eventSlug}
            steps={formInputSteps}
            communityTerms={communityTerms}
            eventInfo={eventInfo}
            allSteps={steps}
            questionnaires={
                typeof questionnaires !== "boolean" ? questionnaires : null
            }
            membershipList={membershipList}
            missingUserData={missingUserData}
            defaultUserValues={defaultUserValues}
            refetchAction={refetchAction}
            customization={customization}
        />
    );
};
